import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Check, Mail, UserPlus, AlertCircle, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { sendEmail, createPlayer } from 'services/send-email.service';
import './styles.scss';
import { Download } from 'lucide-react';
import { useGetTeam, useAddTeam } from 'hooks/team/useTeam';
import { listPlayers } from 'services/send-email.service';

interface ImportParticipantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  isManualMode?: boolean;
  onDownloadTemplate?: () => void;
}

interface Team {
  _id: string;
  name: string;
  extra: Record<string, any>;
  created: number;
  updated: number;
}

interface Participant {
  email: string;
  name: string;
  selected: boolean;
  teamId?: string | null;
}

interface RowError {
  row: number;
  line: number;
  errors: string[];
  data?: { name?: string; email?: string };
}

export const ImportParticipantsModal: React.FC<ImportParticipantsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onDownloadTemplate,
  isManualMode = false,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [importStats, setImportStats] = useState<{ total: number; success: number; errors: number } | null>(null);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para personalização do email
  const [emailSubject, setEmailSubject] = useState('Convite para participar do Ranking');
  const [emailTitle, setEmailTitle] = useState('Você foi convidado!');
  const [emailMessage, setEmailMessage] = useState('Você foi convidado para participar do ranking. Clique no link abaixo para acessar:');

  // Estados para adicionar participante manualmente
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);

  const handleCloseModal = () => {
    setUploadedFile(null);
    setParticipants([]);
    setError('');
    setSuccess('');
    setRowErrors([]);
    setImportStats(null);
    setShowErrorReport(false);
    setShowSuccessModal(false);
    setSuccessMessage('');
    setEmailSubject('Convite para participar do Ranking');
    setEmailTitle('Você foi convidado!');
    setEmailMessage('Você foi convidado para participar do ranking. Clique no link abaixo para acessar:');
    setManualName('');
    setManualEmail('');
    setShowManualForm(false);
    setIsDragging(false);
    onClose();
  };

  // Hooks must be called unconditionally and before any early returns
  const { getTeam } = useGetTeam();
  const { addToTeam } = useAddTeam();
  const [teamData, setTeamData] = useState<Team[]>([]);
  const [players, setPlayers] = useState<any[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await getTeam();
      if (mounted && Array.isArray(data)) setTeamData(data);
    })();
    return () => { mounted = false; };
  }, [getTeam]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const playersData = await listPlayers();
        if (mounted && Array.isArray(playersData)) {
          setPlayers(playersData);
        }
      } catch (error) {
        console.error('Erro ao buscar jogadores:', error);
      }
    })();
    return () => { mounted = false; };
  }, []); 
  if (!isOpen) return null;

  // Validar email
  const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Validar linha individual
  const validateParticipantRow = (
    name: string,
    email: string,
    rowIndex: number,
    seenEmails: Set<string>
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Verificar se a linha está vazia
    if (!name && !email) {
      errors.push('Linha vazia ou sem dados válidos');
      return { isValid: false, errors };
    }

    // Validar nome obrigatório
    if (!name || name.trim() === '') {
      errors.push('Nome é obrigatório');
    }

    // Validar email obrigatório
    if (!email || email.trim() === '') {
      errors.push('Email é obrigatório');
    } else if (!isValidEmail(email)) {
      errors.push(`Email inválido: "${email}"`);
    } else {
      // Verificar duplicatas
      const emailLower = email.trim().toLowerCase();
      if (seenEmails.has(emailLower)) {
        errors.push(`Email duplicado: "${email}" já foi processado anteriormente`);
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = async (file: File) => {
    // Validar extensão do arquivo
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    // Validar MIME type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];

    if (!hasValidExtension && !validTypes.includes(file.type)) {
      setError('❌ Formato de arquivo inválido. Por favor, selecione um arquivo .xlsx, .xls ou .csv');
      setUploadedFile(null);
      return;
    }

    setUploadedFile(file);
    setError('');
    setRowErrors([]);
    setShowErrorReport(false);
    parseSpreadsheetFile(file);
  };

  const parseSpreadsheetFile = async (file: File) => {
    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          }) as any[][];

          if (jsonData.length < 2) {
            setError('A planilha está vazia ou não contém dados válidos');
            return;
          }

          // Processar dados - assume primeira coluna = nome, segunda = email
          const validParticipants: Participant[] = [];
          const errors: RowError[] = [];
          const seenEmails = new Set<string>();
          
          // Criar Set com emails já existentes na base de players
          const existingEmails = new Set(
            players.map((pl) => (pl.email || pl._id || '').toLowerCase())
          );

          // Processar linhas (pular primeira linha se for header)
          const startRow = 1; // Pula header
          
          for (let i = startRow; i < jsonData.length; i++) {
            const row = jsonData[i];
            const nameValue = row[0] ? String(row[0]).trim() : '';
            const emailValue = row[1] ? String(row[1]).trim() : '';

            const validation = validateParticipantRow(nameValue, emailValue, i, seenEmails);

            if (!validation.isValid) {
              errors.push({
                row: i - startRow,
                line: i + 1, // +1 para número real da linha na planilha
                errors: validation.errors,
                data: { name: nameValue, email: emailValue },
              });
            } else {
              // Verificar se o email já existe na base de players
              if (existingEmails.has(emailValue.toLowerCase())) {
                errors.push({
                  row: i - startRow,
                  line: i + 1,
                  errors: [`Email já cadastrado no sistema: "${emailValue}"`],
                  data: { name: nameValue, email: emailValue },
                });
              } else {
                // Adicionar participante válido
                validParticipants.push({
                  name: nameValue,
                  email: emailValue,
                  selected: true,
                  teamId: null,
                });
                
                // Marcar email como visto
                seenEmails.add(emailValue.toLowerCase());
              }
            }
          }

          // Calcular estatísticas
          const totalProcessed = jsonData.length - startRow;
          setImportStats({
            total: totalProcessed,
            success: validParticipants.length,
            errors: errors.length,
          });

          // Se houver erros, mostrar relatório
          if (errors.length > 0) {
            setRowErrors(errors);
            setShowErrorReport(true);
            setParticipants(validParticipants); // Ainda permite ver participantes válidos
          } else {
            setParticipants(validParticipants);
            setShowErrorReport(false);
          }

          if (validParticipants.length === 0 && errors.length > 0) {
            setError('Nenhum participante válido encontrado na planilha');
          } else {
            setError('');
          }
        } catch (parseError) {
          console.error('Erro ao processar planilha:', parseError);
          setError('Erro ao processar planilha. Verifique o formato.');
        }
      };

      reader.onerror = () => {
        setError('Erro ao ler o arquivo');
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Erro ao processar planilha:', error);
      setError('Erro ao processar planilha. Verifique o formato.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleToggleParticipant = (index: number) => {
    const updated = [...participants];
    updated[index].selected = !updated[index].selected;
    setParticipants(updated);
  };

  const handleToggleAll = () => {
    const allSelected = participants.every((p) => p.selected);
    setParticipants(
      participants.map((p) => ({ ...p, selected: !allSelected }))
    );
  };

  const handleRemoveParticipant = (index: number) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const handleChangeTeam = (index: number, teamId: string | null) => {
    const updated = [...participants];
    updated[index].teamId = teamId;
    setParticipants(updated);
  };

  const handleAddManualParticipant = () => {
    if (!manualName.trim() || !manualEmail.trim()) {
      setError('Preencha nome e email do participante');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(manualEmail)) {
      setError('Email inválido');
      return;
    }

    // Verificar se email já existe na lista local de participantes
    if (participants.some(p => p.email.toLowerCase() === manualEmail.toLowerCase())) {
      setError('Este email já foi adicionado à lista');
      return;
    }

    // Verificar se email já existe na base de players (jogadores cadastrados)
    const existingEmails = new Set(
      players.map((pl) => (pl.email || pl._id || '').toLowerCase())
    );
    if (existingEmails.has(manualEmail.toLowerCase())) {
      setError('❌ Este email já está cadastrado no sistema');
      return;
    }

    // Adicionar à lista (tanto no modo manual quanto no import)
    setParticipants([...participants, {
      name: manualName.trim(),
      email: manualEmail.trim(),
      selected: true,
      teamId: null
    }]);

    setManualName('');
    setManualEmail('');
    setError('');
    
    // No modo import, fechar o formulário
    if (!isManualMode) {
      setShowManualForm(false);
    }
  };

  const handleSendInvites = async () => {
    const selectedParticipants = participants.filter((p) => p.selected);

    if (selectedParticipants.length === 0) {
      setError('Selecione pelo menos um participante');
      return;
    }

    if (!emailSubject.trim()) {
      setError('Preencha o assunto do email');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

  const emailTemplate = (name: string, email: string) => `
        <html>
          <body style="font-family: Arial, sans-serif; padding: 20px; background-color: #f4f4f4;">
            <div style="max-width: 600px; margin: 0 auto; background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
              <h2 style="color: #8b5cf6; margin-bottom: 20px;">Olá, ${name}!</h2>
              <h3 style="color: #333; margin-bottom: 15px;">${emailTitle}</h3>
              <p style="color: #666; line-height: 1.6; margin-bottom: 15px;">${emailMessage}</p>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">Para definir sua senha e começar a participar, acesse o link abaixo e escolha uma nova senha:</p>
              <div style="text-align: center; margin: 10px 0 30px 0;">
                <a href="https://rankifier.funifier.com/nova-senha?email=${encodeURIComponent(email)}" style="display: inline-block; padding: 12px 30px; background-color: #f4d58a; color: #5a3e00; text-decoration: none; border-radius: 5px; font-weight: bold;">Definir minha senha</a>
              </div>
              <div style="text-align: center; margin: 10px 0 30px 0;">
                <a href="${window.location.origin}/register" style="display: inline-block; padding: 10px 22px; background-color: #8b5cf6; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">Acessar Ranking</a>
              </div>
              <p style="color: #999; font-size: 12px; margin-top: 30px; border-top: 1px solid #eee; padding-top: 20px;">
                Atenciosamente,<br/>
                <strong>Equipe Rankifier</strong>
              </p>
            </div>
          </body>
        </html>
          `;

      // Criar usuários e enviar emails
      const promises = selectedParticipants.map(async (participant) => {
        try {
          // 1. Criar usuário
          await createPlayer({
            _id: participant.email,
            name: participant.name,
            email: participant.email,
            extra: {
              conviteEnviado: false
            }
          });

          // 2. Enviar email
          await sendEmail({
            from: 'noreply@rankifier.com',
            to: participant.email,
            subject: emailSubject,
            html: emailTemplate(participant.name, participant.email),
          });

          // 3. Adicionar ao time (síncrono, após as outras operações)
          if (participant.teamId) {
            await addToTeam(participant.teamId, participant.email);
          }

          return { success: true, email: participant.email };
        } catch (error) {
          console.error(`Erro ao processar ${participant.email}:`, error);
          return { success: false, email: participant.email };
        }
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;

      setLoading(false);

      if (successCount > 0) {
        // Mostrar modal de sucesso
        setSuccessMessage(`${successCount} de ${selectedParticipants.length} convite(s) enviado(s) com sucesso!`);
        setShowSuccessModal(true);
        
        // Fechar após 3 segundos
        setTimeout(() => {
          onSuccess?.();
          handleCloseModal();
        }, 3000);
      } else {
        setError('Nenhum convite foi enviado. Verifique o console para detalhes.');
      }

      if (successCount < selectedParticipants.length) {
        console.warn('Alguns convites falharam:', results.filter(r => !r.success));
      }
    } catch (error: any) {
      setLoading(false);
      setError(error.message || 'Erro ao enviar convites');
    }
  };

  const handleSendClick = () => {
    if (loading) {
      setError('Envio em andamento. Aguarde até terminar.');
      return;
    }

    if (participants.length === 0) {
      setError('Adicione ao menos um participante para enviar um convite.');
      return;
    }

    // Verificar participantes selecionados
    const selectedParticipants = participants.filter(p => p.selected);
    if (selectedParticipants.length === 0) {
      setError('Selecione pelo menos um participante para enviar um convite.');
      return;
    }

    // Verificar se algum participante selecionado já existe na base de players
    const existingEmails = new Set(
      players.map((pl) => (pl.email || pl._id || '').toLowerCase())
    );
    
    // Encontrar todos os participantes com emails duplicados
    const duplicateParticipants = selectedParticipants.filter(p => 
      existingEmails.has(p.email.toLowerCase())
    );
    
    if (duplicateParticipants.length > 0) {
      // Remover os participantes duplicados da lista
      const updatedParticipants = participants.filter(p => 
        !duplicateParticipants.some(dup => dup.email.toLowerCase() === p.email.toLowerCase())
      );
      setParticipants(updatedParticipants);
      
      // Mostrar mensagem de erro com os emails removidos
      const duplicateEmails = duplicateParticipants.map(p => p.email).join(', ');
      setError(
        `❌ ${duplicateParticipants.length === 1 ? 'Email já cadastrado' : 'Emails já cadastrados'}: ${duplicateEmails}. ${duplicateParticipants.length === 1 ? 'O participante foi removido' : 'Os participantes foram removidos'} da lista.`
      );
      return;
    }

    // Tudo certo — limpar mensagem e disparar o envio
    setError('');
    handleSendInvites();
  };

  const handleDownloadErrorReport = () => {
    if (rowErrors.length === 0) return;

    const reportLines = [
      '=== RELATÓRIO DE ERROS DE IMPORTAÇÃO DE PARTICIPANTES ===\n',
      `Data: ${new Date().toLocaleString('pt-BR')}\n`,
      `Arquivo: ${uploadedFile?.name}\n`,
      `Total de linhas: ${importStats?.total || 0}\n`,
      `Linhas com sucesso: ${importStats?.success || 0}\n`,
      `Linhas com erro: ${importStats?.errors || 0}\n`,
      '\n=== DETALHES DOS ERROS ===\n\n',
    ];

    rowErrors.forEach((error) => {
      reportLines.push(`📍 Linha ${error.line}:\n`);
      error.errors.forEach((err) => {
        reportLines.push(`   ❌ ${err}\n`);
      });
      if (error.data) {
        reportLines.push(`   📄 Nome: ${error.data.name || '(vazio)'}\n`);
        reportLines.push(`   📄 Email: ${error.data.email || '(vazio)'}\n`);
      }
      reportLines.push('\n');
    });

    const blob = new Blob([reportLines.join('')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-erros-participantes-${new Date().getTime()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCloseErrorReport = () => {
    setShowErrorReport(false);
  };

  const handleIgnoreErrorsAndContinue = () => {
    setShowErrorReport(false);
    setError('');
  };

  return (
    <div className="import-participants-modal-overlay" onClick={handleCloseModal}>
      <div className="import-participants-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{isManualMode ? 'Adicionar Participante Manualmente' : 'Importar Participantes'}</h2>
          <button className="modal-close" onClick={handleCloseModal}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {/* Seção de upload ou adicionar manualmente */}
          {!isManualMode && (
            <div className="add-participants-section">
              <h3 className="section-title">Adicionar participantes</h3>
              
              <div className="add-options">
                <div
                  className={`upload-area ${isDragging ? 'dragging' : ''} ${uploadedFile ? 'has-file' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !uploadedFile && fileInputRef.current?.click()}
                >
                  {uploadedFile ? (
                    <div className="file-uploaded-inline">
                      <Check size={24} className="check-icon" />
                      <span className="file-name">{uploadedFile.name}</span>
                      <button
                        className="change-file-button-inline"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Trocar
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} />
                      <p className="upload-text">Arraste e solte sua planilha aqui</p>
                      <p className="upload-subtext">ou clique para selecionar</p>
                      <p className="upload-formats">Formatos: .xlsx, .xls, .csv</p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Formulário manual (modo manual ou expandido) */}
          {(isManualMode || showManualForm) && (
            <div className="add-participants-section">
              {!isManualMode && <h3 className="section-title">Adicionar participante</h3>}
              {isManualMode && <h3 className="section-title">Adicionar participantes</h3>}
              <div className="manual-form">
                <div className="form-row-with-button">
                  <div className="form-group">
                    <label className="form-label">Nome completo</label>
                    <input
                      type="text"
                      className="form-input"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="Ex: João Silva"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && isManualMode) {
                          e.preventDefault();
                          handleAddManualParticipant();
                        }
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">E-mail</label>
                    <input
                      type="email"
                      className="form-input"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="Ex: joao@example.com"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && isManualMode) {
                          e.preventDefault();
                          handleAddManualParticipant();
                        }
                      }}
                    />
                  </div>
                  {isManualMode && (
                    <button
                      className="btn-add-inline mb-5"
                      onClick={handleAddManualParticipant}
                      title="Adicionar participante"
                      type="button"
                    >
                      <UserPlus size={20} />
                    </button>
                  )}
                </div>
                {!isManualMode && (
                  <div className="manual-form-actions">
                    <button
                      className="btn-cancel-manual"
                      onClick={() => {
                        setShowManualForm(false);
                        setManualName('');
                        setManualEmail('');
                        setError('');
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-add-manual-confirm"
                      onClick={handleAddManualParticipant}
                    >
                      <UserPlus size={16} />
                      Adicionar
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          
          

          {/* Lista de participantes */}
          {participants.length > 0 && (
            <div className="participants-section">
              <div className="participants-header">
                <h3 className="participants-title">
                  {isManualMode ? `Participantes a enviar (${participants.length})` : `Participantes encontrados (${participants.filter(p => p.selected).length} selecionados)`}
                </h3>
                {!isManualMode && (
                  <button className="select-all-button" onClick={handleToggleAll}>
                    {participants.every((p) => p.selected) ? 'Desmarcar todos' : 'Selecionar todos'}
                  </button>
                )}
              </div>

              <div className="participants-list">
                {participants.map((participant, index) => (
                  <div key={index} className="participant-item">
                    {!isManualMode && (
                      <input
                        type="checkbox"
                        checked={participant.selected}
                        onChange={() => handleToggleParticipant(index)}
                        className="participant-checkbox"
                      />
                    )}
                    <div className="participant-info">
                      <span className="participant-name">{participant.name}</span>
                      <span className="participant-email">{participant.email}</span>
                      <div className="participant-team-selector">
                        <label className="team-selector-label">Time:</label>
                        <select
                          value={participant.teamId || ''}
                          onChange={(e) => handleChangeTeam(index, e.target.value || null)}
                          className="team-selector-dropdown"
                        >
                          <option value="">Não adicionar a nenhum time</option>
                          {teamData.map((team) => (
                            <option key={team._id} value={team._id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {isManualMode && (
                      <button
                        className="btn-remove-participant"
                        onClick={() => handleRemoveParticipant(index)}
                        title="Remover participante"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message mb-4">
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="success-message mb-4">
              <span>{success}</span>
            </div>
          )}

          {/* Relatório de Erros */}
          {showErrorReport && rowErrors.length > 0 && (
            <div className="error-report-container">
              <div className="error-report-header">
                <div className="error-stats-mini">
                  <div className="stat-mini stat-total-mini">
                    <FileSpreadsheet size={20} />
                    <span>{importStats?.total || 0} linhas</span>
                  </div>
                  <div className="stat-mini stat-success-mini">
                    <CheckCircle2 size={20} />
                    <span>{importStats?.success || 0} válidas</span>
                  </div>
                  <div className="stat-mini stat-error-mini">
                    <AlertCircle size={20} />
                    <span>{importStats?.errors || 0} com erro</span>
                  </div>
                </div>
                <div className="error-report-actions">
                  <button className="btn-download-report-mini" onClick={handleDownloadErrorReport}>
                    <Upload size={14} />
                    Baixar Relatório
                  </button>
                  <button className="btn-close-report" onClick={handleCloseErrorReport}>
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="error-list-compact">
                {rowErrors.slice(0, 5).map((error, index) => (
                  <div key={index} className="error-item-compact">
                    <div className="error-item-header-compact">
                      <span className="error-line-number-compact">Linha {error.line}</span>
                      <span className="error-count-compact">{error.errors.length} erro(s)</span>
                    </div>
                    <ul className="error-messages-compact">
                      {error.errors.map((err, idx) => (
                        <li key={idx}>
                          <AlertCircle size={12} />
                          {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {rowErrors.length > 5 && (
                  <p className="error-list-footer">
                    E mais {rowErrors.length - 5} erro(s)... Baixe o relatório completo.
                  </p>
                )}
              </div>

              <div className="error-actions-footer">
                <AlertCircle size={16} />
                <p>
                  {importStats?.success || 0} participante(s) válido(s) foi(ram) carregado(s). 
                  Você pode enviar convites apenas para eles ou corrigir a planilha.
                </p>
                <button className="btn-ignore-errors" onClick={handleIgnoreErrorsAndContinue}>
                  Continuar com participantes válidos
                </button>
              </div>
            </div>
          )}
          
            <div className="email-customization">
              <h3 className="customization-title">Personalizar convite</h3>
              <div className="email-info">
                <Mail className='text-white' size={20} />
                <p className='text-white'>Este e-mail será enviado para {isManualMode ? `${participants.length} participante${participants.length > 1 ? 's' : ''}` : 'todos os participantes selecionados'} acima com o nome personalizado de cada um.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Assunto do e-mail</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Ex: Convite para participar do Ranking"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Título da mensagem</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailTitle}
                  onChange={(e) => setEmailTitle(e.target.value)}
                  placeholder="Ex: Você foi convidado!"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mensagem principal</label>
                <textarea
                  className="form-textarea"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Ex: Você foi convidado para participar do ranking..."
                  rows={3}
                />
              </div>
            </div>
        </div>

        <div className="modal-footer">
          { !isManualMode && (
            <button onClick={onDownloadTemplate} className="flex bg-[#2563eb] rounded-lg p-4 text-white font-semibold cursor-pointer
          transition hover:shadow-[0_6px_20px_rgba(59,130,246,0.5)] hover:-translate-y-0.5" disabled={loading}>
            <Download size={18} className='mt-0.5 mr-2'/>
            Baixar planilha modelo
          </button> 
          )}
          <button className="cancel-button" onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </button>
          {isManualMode ? (
            <button
              className="send-button"
              onClick={handleSendClick}
              aria-disabled={loading || participants.length === 0}
              >
              <Mail size={16} />
              {loading ? 'Enviando...' : `Enviar convites (${participants.length})`}
            </button>
          ) : (
            <button
              className="send-button"
              onClick={handleSendClick}
              aria-disabled={loading || participants.length === 0 || participants.every(p => !p.selected)}
              >
              {loading ? 'Enviando...' : 'Enviar convites'}
            </button>
          )}
        </div>
      </div>

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="success-modal-overlay-participants">
          <div className="success-modal-participants">
            <div className="success-icon-participants">
              <CheckCircle2 size={56} strokeWidth={2.5} />
            </div>
            <h2 className="success-title-participants">Convites Enviados!</h2>
            <p className="success-message-participants">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportParticipantsModal;
